"use client";

import { useState,useMemo } from "react";
import { format, subMonths } from "date-fns";
import { CalendarIcon, Filter, RotateCcw } from "lucide-react";
import { Button } from "components/ui/button_v2";
import { Calendar } from "components/ui/calendar_v2";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover_v2";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select_v4";
import { cn } from "lib/utils";
import localization from "services/localization";

const TIME_PRESETS = [
  { label: localization.last_7_days || "Last 7 days", value: "7" },
  { label: localization.last_15_days || "Last 15 days", value: "15" },
  { label: localization.last_30_days || "Last 30 days", value: "30" },
  { label: localization.last_90_days || "Last 90 days", value: "90" },
  { label: localization.custom || "Custom", value: "custom" },
];

export default function AnalyticsFilters({
  filters,
  onFilterChange,
  users,
  departments,
  availableRoles,
  theme,
}) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
const [userSearch, setUserSearch] = useState("");

  const today = new Date();
  const oneMonthAgo = subMonths(today, 1);

  const defaultRange = {
    from: oneMonthAgo,
    to: today,
  };

  const select_bg =
    theme === "dark"
      ? "bg-[var(--card-bg)] text-white border-border"
      : "bg-card text-foreground border-border";

  const handleReset = () => {
    onFilterChange({
      role: "all",
      user: "all",
      department: "all",
      timePreset: "custom",
      dateRange: defaultRange,
    });
  };

const availableUsers = useMemo(() => {
  let u = [...users];

  if (userSearch.trim()) {
    const q = userSearch.toLowerCase();
    u = u.filter(
      (usr) =>
        usr.name?.toLowerCase().includes(q) ||
        usr.email?.toLowerCase().includes(q)
    );
  }

  return u;
}, [users, userSearch]);

const selectedUserObj = useMemo(
  () => users.find((u) => u.id === filters.user),
  [users, filters.user]
);

  return (
    <div className="flex flex-col gap-4">
      <div className={"flex items-center gap-2"+select_bg}>
        <Filter className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">{localization.filters || "Filters"}</h3>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {/* Role Filter */}
        <div className="flex flex-col gap-1.5">
          <label className={"text-xs font-medium text-muted-foreground"+select_bg}>
            {localization.user_role || "User Role"}
          </label>
          <Select
            value={filters.role}
            onValueChange={(val) => onFilterChange({ ...filters, role: val })}
          >
            <SelectTrigger className={cn("w-[160px] h-9 text-sm", select_bg)}>
              <SelectValue placeholder={localization.all_roles || "All Roles"} />
            </SelectTrigger>
            <SelectContent theme={theme}>
              {availableRoles.map(role => (
    <SelectItem key={role} value={role} theme={theme}>
      {role.charAt(0).toUpperCase() + role.slice(1)}
    </SelectItem>
  ))}
            </SelectContent>
          </Select>
        </div>

  {/* Department Filter */}
        <div className="flex flex-col gap-1.5">
          <label className={"text-xs font-medium text-muted-foreground"+select_bg}>
            {localization.department || "Department"}
          </label>
          <Select
            value={filters.department}
            onValueChange={(val) =>
              onFilterChange({ ...filters, department: val  })
            }
          >
            <SelectTrigger className={cn("w-[180px] h-9 text-sm", select_bg)}>
              <SelectValue placeholder={localization.all_departments || "All Departments"} />
            </SelectTrigger>
            <SelectContent theme={theme}>
              <SelectItem value="all" theme={theme}>{localization.all_departments || "All Departments"}</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id} theme={theme}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User Filter */}
        <div className="flex flex-col gap-1.5">
          <label className={"text-xs font-medium text-muted-foreground"+select_bg}>
            {localization.user || "User"}
          </label>
          <Select
            value={filters.user}
            onValueChange={(val) => {onFilterChange({ ...filters, user: val })}}
          >
            <SelectTrigger className={cn("w-[180px] h-9 text-sm", select_bg)}>
             <SelectValue>
  {filters.user === "all"
    ? localization.all_users || "All Users"
    : selectedUserObj?.name || "Unknown"}
</SelectValue>
            </SelectTrigger>
            <SelectContent theme={theme}>
               <div className="p-2 border-b">
    <input
      type="text"
      placeholder="Search user..."
      value={userSearch}
      onChange={(e) => setUserSearch(e.target.value)}
      className={cn(
        "w-full px-2 py-1 text-sm rounded-md outline-none",
        theme === "dark"
          ? "bg-gray-800 text-white"
          : "bg-gray-100 text-black"
      )}
    />
  </div>

              <SelectItem value="all" theme={theme}>{localization.all_users || "All Users"}</SelectItem>
              {availableUsers.map((u) => (
                <SelectItem key={u.id} value={u.id} theme={theme}>
    <div className="flex flex-col">
      <span>{u.name}</span>
      <span className="text-xs text-muted-foreground">{u.email}</span>
    </div>
  </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Time Preset */}
        <div className="flex flex-col gap-1.5">
          <label className={"text-xs font-medium text-muted-foreground"+select_bg}>
            {localization.time_period || "Time Period"}
          </label>
          <Select
            value={filters.timePreset}
            onValueChange={(val) =>
              onFilterChange({ ...filters, timePreset: val })
            }
          >
            <SelectTrigger className={cn("w-[150px] h-9 text-sm", select_bg)}>
              <SelectValue placeholder="Select Period" />
            </SelectTrigger>
            <SelectContent theme={theme}>
              {TIME_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value} theme={theme}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Range Picker */}
        {filters.timePreset === "custom" && (
          <div className="flex flex-col gap-1.5">
            <label className={"text-xs font-medium text-muted-foreground"+select_bg}>
              {localization.date_range || "Date Range"}
            </label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[260px] h-9 justify-start text-left text-sm font-normal",
                    !filters.dateRange?.from && "text-muted-foreground",
                    select_bg
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateRange?.from ? (
                    filters.dateRange.to ? (
                      <>
                        {format(filters.dateRange.from, "LLL dd, y")} -{" "}
                        {format(filters.dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(filters.dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </Button>
              </PopoverTrigger>
<PopoverContent className="w-auto p-2" theme={theme}>
<div className={`rounded-md p-3 ${theme === 'dark' ? 'bg-[#1e1e2e]' : 'bg-white'}`}>

                <Calendar
                theme={theme}
                  initialFocus
                  mode="range"
                  defaultMonth={filters.dateRange?.from}
                  selected={filters.dateRange}
                  onSelect={(range) =>
                    onFilterChange({ ...filters, dateRange: range })
                  }
                  numberOfMonths={2}
                />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Reset Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          className={"h-9 text-muted-foreground hover:text-foreground last:mr-0"+select_bg}
        >
          <RotateCcw className=" h-3.5 w-3.5" />
          {localization.reset || "Reset"}
        </Button>
      </div>
    </div>
  );
}
